import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ShieldAlert, ExternalLink } from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';

const ROTATION = {
  date: 'October 22, 2026',
  time: '17:53:23 UTC',
  block: '52951128',
  tx: '0x9070b2858f30be20a4d63e96a607c8f93cbf268e4bf98c27bbfcef70d4460baa',
  oracle: '0x2364493206Aa7435050780bf710F934189B51ed6',
  oldSigner: '0xDCB21cC5Fc3d335D79717aF2C2404F5c622c3441',
  newSigner: '0x532cedA31eB2A775f8763C42d48bd94e2cC456d3',
};
const scan = (kind, v) => `https://basescan.org/${kind}/${v}`;

function Field({ label, value, href, id }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-2.5 border-b border-white/5">
      <span className="text-xs uppercase tracking-widest text-gray-500 sm:w-40 shrink-0">{label}</span>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="font-mono text-sm text-amber-200 hover:text-amber-100 break-all inline-flex items-center gap-1" data-testid={id}>{value} <ExternalLink className="w-3 h-3 shrink-0" /></a>
      ) : <span className="font-mono text-sm text-white break-all" data-testid={id}>{value}</span>}
    </div>
  );
}

export default function Security() {
  const r = ROTATION;
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="security-page">
      <Helmet><title>Security Notices | Kojiru</title></Helmet>
      <Navbar />
      <main className="pt-28 pb-20 px-6">
        <div className="container mx-auto max-w-3xl">
          <div className="text-xs uppercase tracking-widest text-amber-300 mb-3">Security notices</div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-4" data-testid="security-title">Security notices</h1>
          <p className="text-base text-gray-400 leading-relaxed mb-10">Dated notices about keys and contracts Kojiru publishes. Newest first. You can check every on-chain fact here yourself.</p>

          <article className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.04] p-6 sm:p-8" data-testid="security-notice-signer-rotation">
            <div className="flex items-center gap-2 text-amber-300 text-sm mb-2"><ShieldAlert className="w-4 h-4" /> {r.date}, {r.time}</div>
            <h2 className="text-xl md:text-2xl font-semibold mb-4">Score oracle signer key rotated</h2>
            <p className="text-sm text-gray-300 leading-relaxed mb-5">Kojiru replaced the key that signs score updates for its score oracle on Base mainnet.</p>
            <Field label="Oracle contract" value={r.oracle} href={scan('address', r.oracle)} id="security-oracle" />
            <Field label="Old signer, retired" value={r.oldSigner} id="security-old-signer" />
            <Field label="New signer" value={r.newSigner} id="security-new-signer" />
            <Field label="Transaction" value={r.tx} href={scan('tx', r.tx)} id="security-tx" />
            <Field label="Block" value={r.block} href={scan('block', r.block)} id="security-block" />
            <h3 className="font-semibold mt-6 mb-2">Why</h3>
            <p className="text-sm text-gray-300 leading-relaxed">A hosting provider we use reported that stored environment variables may have been accessed. We are treating every key stored there as exposed and replacing all of them. This is a precaution.</p>
            <h3 className="font-semibold mt-6 mb-2">What it means for you</h3>
            <ol className="list-decimal pl-5 space-y-1.5 text-sm text-gray-300" data-testid="security-what-it-means">
              <li>From block {r.block}, the oracle accepts only signatures from the new signer.</li>
              <li>Treat any record or packet signed by the old signer after {r.date}, {r.time} as not valid.</li>
              <li>Records the old signer made before the provider's incident are unchanged.</li>
              <li>We checked the oracle's on-chain events. The old signer made no score updates between the incident and the rotation. Before the rotation, the last events date from early September 2026.</li>
              <li>Our Base Sepolia test contracts were redeployed with new keys. The earlier Sepolia addresses are retired.</li>
            </ol>
            <p className="text-sm text-gray-400 mt-6">Check it yourself: open the oracle contract on BaseScan, choose Read Contract, and call <span className="font-mono text-gray-200">signer</span>. Questions: <a href="mailto:wayne@kojiru.com" className="text-amber-200 hover:text-amber-100">wayne@kojiru.com</a></p>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
}
